'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface MetricCounterProps {
  value: number
  suffix?: string
  prefix?: string
  label: string
  duration?: number
  className?: string
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

function AnimatedCounter({
  value,
  suffix = '',
  prefix = '',
  label,
  duration = 2000,
  className,
}: MetricCounterProps) {
  const [displayValue, setDisplayValue] = useState(0)
  const frameRef = useRef<number>(0)
  const startTimeRef = useRef<number>(0)
  const hasStarted = useRef(false)

  useEffect(() => {
    if (hasStarted.current) return
    hasStarted.current = true

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp
      const elapsed = timestamp - startTimeRef.current
      const progress = Math.min(elapsed / duration, 1)
      const easedProgress = easeOutCubic(progress)

      setDisplayValue(Math.round(easedProgress * value))

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate)
      }
    }

    // Small delay before starting animation
    const timer = setTimeout(() => {
      frameRef.current = requestAnimationFrame(animate)
    }, 300)

    return () => {
      clearTimeout(timer)
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    }
  }, [value, duration])

  return (
    <div className={cn('text-center', className)}>
      <p className='text-2xl font-bold text-white lg:text-3xl'>
        {prefix}
        {displayValue}
        {suffix}
      </p>
      <p className='mt-1 text-xs text-white/60'>{label}</p>
    </div>
  )
}

const METRICS = [
  { value: 90, suffix: '%+', label: 'STP Rate' },
  { value: 23, label: 'Days DSO Reduction' },
  { value: 95, suffix: '%', label: 'Dispute Accuracy' },
  { value: 40, suffix: '%', label: 'Productivity Boost' },
]

export function MetricCounters({ className }: { className?: string }) {
  return (
    <div className={cn('grid grid-cols-4 gap-6', className)}>
      {METRICS.map((m) => (
        <AnimatedCounter
          key={m.label}
          value={m.value}
          suffix={m.suffix}
          label={m.label}
        />
      ))}
    </div>
  )
}
